import React, { useEffect, useRef, useState } from 'react';
import { mediaApi } from '../api/media';
import { getErrorMessage } from '../api/client';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import { IconCamera, IconTrash } from './ui/Icons';

export default function CoverUploader({ eventId }) {
  const [cover, setCover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await mediaApi.getCover(eventId);
      setCover(data.cover);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [eventId]);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const media = await mediaApi.uploadCover(eventId, file, setProgress);
      setCover(media);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar la foto de portada?')) return;
    try {
      await mediaApi.deleteCover(eventId);
      setCover(null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-slate-800">Foto de portada</h3>
        <p className="text-xs text-slate-500">
          Aparecerá al abrir la invitación, antes de entrar al contenido.
        </p>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="grid place-items-center py-6">
          <Spinner className="text-slate-400" />
        </div>
      ) : cover ? (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="relative aspect-[4/5] sm:aspect-[16/10] bg-slate-100">
            <img src={cover.url} alt="Portada" className="w-full h-full object-cover" />
          </div>
          <div className="p-4 flex items-center justify-between bg-white">
            <div>
              <p className="text-sm font-semibold text-slate-800">Portada cargada</p>
              <p className="text-xs text-slate-500">Se muestra al abrir la invitación</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors">
                Cambiar
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
              <button
                onClick={handleDelete}
                aria-label="Eliminar portada"
                className="w-9 h-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <span className="block w-4 h-4">
                  <IconTrash className="w-full h-full" />
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-slate-900 hover:bg-slate-50 transition-colors cursor-pointer">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          {uploading ? (
            <div className="space-y-2">
              <Spinner size="sm" className="text-slate-400 mx-auto" />
              <p className="text-xs text-slate-500">Subiendo {progress}%</p>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-slate-100 grid place-items-center text-slate-500">
                <span className="block w-6 h-6">
                  <IconCamera className="w-full h-full" />
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-700">Subir foto de portada</p>
              <p className="text-xs text-slate-500 mt-1">JPG, PNG o WebP · máx 8 MB · recomendado 1200×1500</p>
            </>
          )}
        </label>
      )}
    </div>
  );
}